import type { NavigateFunction } from 'react-router-dom'

type AnyErrorShape = {
	status?: number
	message?: string
	response?: {
		status?: number
		data?: any
	}
	cause?: unknown
}

function asAnyError(error: unknown): AnyErrorShape | undefined {
	if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
		return undefined
	}
	return error as AnyErrorShape
}

export function getHttpStatus(error: unknown): number | undefined {
	const err = asAnyError(error)
	const status =
		err?.response?.status ??
		err?.status ??
		asAnyError(err?.cause)?.response?.status ??
		asAnyError(err?.cause)?.status
	return typeof status === 'number' ? status : undefined
}

/**
 * Some endpoints return { status_code: 403 } in the response body in addition to HTTP 403.
 */
export function getApiStatusCode(error: unknown): number | undefined {
	const err = asAnyError(error)
	const raw =
		err?.response?.data?.status_code ??
		err?.response?.data?.statusCode ??
		asAnyError(err?.cause)?.response?.data?.status_code ??
		asAnyError(err?.cause)?.response?.data?.statusCode

	if (typeof raw === 'number') return raw
	if (typeof raw === 'string') {
		const parsed = Number(raw)
		return Number.isFinite(parsed) ? parsed : undefined
	}
	return undefined
}

export function isForbiddenError(error: unknown): boolean {
	const httpStatus = getHttpStatus(error)
	if (httpStatus === 403) return true

	const apiStatus = getApiStatusCode(error)
	if (apiStatus === 403) return true

	// Fallback: sometimes the thrown error is a plain Error with a message.
	const message =
		asAnyError(error)?.message ?? asAnyError(asAnyError(error)?.cause)?.message
	return typeof message === 'string' && /status code 403/i.test(message)
}

/**
 * Checks if an error is either a 403 (Forbidden) or 404 (Not Found) error.
 * Useful when switching accounts - resource may be forbidden or not exist for the new account.
 */
export function isAccessDeniedError(error: unknown): boolean {
	const httpStatus = getHttpStatus(error)
	if (httpStatus === 403 || httpStatus === 404) return true

	const apiStatus = getApiStatusCode(error)
	if (apiStatus === 403 || apiStatus === 404) return true

	// Fallback: sometimes the thrown error is a plain Error with a message.
	const message =
		asAnyError(error)?.message ?? asAnyError(asAnyError(error)?.cause)?.message
	return (
		typeof message === 'string' &&
		(/status code 403/i.test(message) || /status code 404/i.test(message))
	)
}

function schedule(fn: () => void) {
	if (typeof queueMicrotask === 'function') {
		queueMicrotask(fn)
		return
	}
	setTimeout(fn, 0)
}

/**
 * Factory to reuse the same onError handler across hooks.
 * Use it when invoking a hook, e.g. options: { onError: createRedirectOnForbidden(navigate, '/music') }
 */
export function createRedirectOnForbidden(
	navigate: NavigateFunction,
	redirectTo: string,
	{ replace = true }: { replace?: boolean } = {}
): (error: unknown) => void {
	return (error: unknown) => {
		if (!isForbiddenError(error)) return
		schedule(() => navigate(redirectTo, { replace }))
	}
}

/**
 * Factory to reuse the same onError handler across hooks for access denied scenarios.
 * Redirects on both 403 (Forbidden) and 404 (Not Found) errors.
 * Useful when switching accounts where resources may be forbidden or not exist for the new account.
 * Use it when invoking a hook, e.g. options: { onError: createRedirectOnAccessDenied(navigate, '/music') }
 */
export function createRedirectOnAccessDenied(
	navigate: NavigateFunction,
	redirectTo: string,
	{ replace = true }: { replace?: boolean } = {}
): (error: unknown) => void {
	return (error: unknown) => {
		if (!isAccessDeniedError(error)) return
		schedule(() => navigate(redirectTo, { replace }))
	}
}

/**
 * Smart retry function for React Query that skips retries for 403/404 errors
 * but allows retries for recoverable errors (network issues, 429, 504, etc.)
 *
 * Use in React Query options: { retry: shouldRetryRequest }
 *
 * @param failureCount - Number of times the request has failed
 * @param error - The error that occurred
 * @returns boolean - Whether to retry the request
 */
export function shouldRetryRequest(
	failureCount: number,
	error: unknown
): boolean {
	// Don't retry on 403/404 - these are access denied errors
	if (isAccessDeniedError(error)) {
		return false
	}

	// Default retry behavior for other errors (up to 3 times)
	return failureCount < 3
}
